import { Nullable, StrictExclude } from '@crossbeam/types';

import { differenceBy, isEqual, sortBy } from 'lodash';
import { storeToRefs } from 'pinia';
import { v4 as uuidv4 } from 'uuid';
import {
  MaybeRef,
  computed,
  onBeforeMount,
  onBeforeUnmount,
  ref,
  toValue,
  watch,
} from 'vue';
import { useRoute } from 'vue-router';

import { crossbeamApi } from '@/api';
import { captureException } from '@/errors';
import { useFeedsStore, useSourcesStore } from '@/stores';
import { useRootStore } from '@/stores/RootStore';
import {
  BusinessSettingDefinition,
  CoercedOperand,
  NewExistingPopulationPrefix,
  OperandOption,
  OperandsLookup,
  OpportunityFiltersPayload,
  SourceOption,
} from '@/types/business_settings';
import { CRMFeedType } from '@/types/feeds';
import { FilterableSource, SourceField } from '@/types/sources';

import { usePreferredCRMFeed } from './usePreferredCRMFeed';

const businessSettings = ref<BusinessSettingDefinition[]>([]);

export function __resetBusinessSettings() {
  businessSettings.value = [];
}

export default function useBusinessSettings(isDisabled?: MaybeRef<boolean>) {
  const sourcesStore = useSourcesStore();
  const { updateOrgInfo } = useRootStore();
  const { preferredCRMFeed } = usePreferredCRMFeed();
  const route = useRoute();

  const loading = ref(false);
  const selectedFeedId = ref<Nullable<number>>(null);
  const selectedOpportunityFieldId = ref<Nullable<number>>(null);
  const selectedOperandsLookup = ref<OperandsLookup>({
    newBusiness: [],
    existingBusiness: [],
  });
  const opportunityFieldOptions = ref<SourceOption[]>([]);
  const operandOptions = ref<OperandOption[]>([]); // operands are the values of the selected source field
  const disabled = computed(
    () =>
      (!selectedFeedId.value && !opportunityFieldOptions.value.length) ||
      toValue(isDisabled),
  );

  const snapshot = ref<{
    feedId: Nullable<number>;
    fieldId: Nullable<number>;
    operands: OperandsLookup;
  } | null>(null);

  const isDataSourceInputDisabled = computed(() => crmFeeds.value.length === 1);

  function resetOperands() {
    selectedOperandsLookup.value.newBusiness = [];
    selectedOperandsLookup.value.existingBusiness = [];
  }

  async function resetAll() {
    resetOperands();
    selectedOpportunityFieldId.value = null;
    opportunityFieldOptions.value = [];
    operandOptions.value = [];
    await crossbeamApi.PUT('/v0.1/opportunity-filters', {
      body: { items: [] },
    });
  }

  function filterOperandOptions(
    excludedValues: string[],
    options: OperandOption[],
  ) {
    return differenceBy(
      options,
      excludedValues.map((val) => ({ value: val })),
      'value',
    );
  }

  const isMultiSelect = computed(
    () => selectedSourceField.value?.dataType === 'string',
  );

  function createAndDiffBusinessOptions(key: keyof OperandsLookup) {
    return isMultiSelect.value
      ? filterOperandOptions(
          selectedOperandsLookup.value[key] as string[],
          operandOptions.value,
        )
      : operandOptions.value;
  }

  const newBusinessOptions = computed(() =>
    createAndDiffBusinessOptions('existingBusiness'),
  );

  const recurringBusinessOptions = computed(() =>
    createAndDiffBusinessOptions('newBusiness'),
  );

  const selectedSourceField = computed(() =>
    opportunityFieldOptions.value.find(
      (field) => field.value === selectedOpportunityFieldId.value,
    ),
  );

  function getDataSourceSvg(integrationType: string) {
    let name = integrationType;
    if (name === 'hubspot_v3') name = 'hubspot';
    return `${name}Icon`;
  }

  const feedsStore = useFeedsStore();
  const { crmFeeds } = storeToRefs(feedsStore);
  const feedOptions = computed(() =>
    crmFeeds.value.map((feed) => ({
      id: feed.id,
      label: feed.integration.friendly_name,
      name: feed.integration.friendly_name,
      value: feed.id,
      svg: getDataSourceSvg(feed.integration.type),
    })),
  );

  const selectedFeedType = computed(
    () =>
      crmFeeds.value.find((f) => f.id === selectedFeedId.value)?.integration
        .type as CRMWithPickList,
  );

  const dealSourceFromSelectedFeed = computed(() =>
    sourcesStore.dealSources.find((source) => {
      return source.feed_id === selectedFeedId.value;
    }),
  );

  const isValidSettings = computed(() => {
    const { newBusiness, existingBusiness } = selectedOperandsLookup.value;
    const hasFeedAndFieldSelected =
      !!selectedFeedId.value && !!selectedOpportunityFieldId.value;

    const hasNew = Array.isArray(newBusiness)
      ? newBusiness.length > 0
      : !!newBusiness;

    const hasExisting = Array.isArray(existingBusiness)
      ? existingBusiness.length > 0
      : !!existingBusiness;

    return (
      (hasFeedAndFieldSelected && hasNew && hasExisting) ||
      !selectedOpportunityFieldId.value
    );
  });

  const createFilterParts = (label: string, inOperand: CoercedOperand) => {
    /* we use "true" and "false" strings to play well with select component, coercing to Boolean for final payload */
    const outOperand = Array.isArray(inOperand)
      ? inOperand
      : [JSON.parse(inOperand) as boolean];

    return [
      {
        source_field_id: selectedOpportunityFieldId.value as number,
        label,
        operator: 'in' as const,
        operand: outOperand,
      },
    ];
  };

  function getAccountSourceId() {
    const accountSources = sourcesStore
      .getSourcesByMdmType('account')
      .filter((source) => source.feed_id === selectedFeedId.value)
      .at(0)?.id;

    return accountSources;
  }
  const createPayloadItem = (
    nameSuffix: NewExistingPopulationPrefix,
    uuid: string,
    operand: CoercedOperand,
  ) => {
    const accountSourceId = getAccountSourceId();
    if (!dealSourceFromSelectedFeed.value?.id || !accountSourceId) return null;

    return {
      name: `${nameSuffix}-${selectedFeedId.value}`,
      source_id: accountSourceId,
      source_filter_expressions: {
        [`${dealSourceFromSelectedFeed.value?.table}`]: {
          filter_expression: [uuid],
          filter_parts: createFilterParts(uuid, operand),
        },
      },
    };
  };

  function buildPayload() {
    const newBusinessUUID = uuidv4();
    const recurringBusinessUUID = uuidv4();

    const newBusinessSetting = createPayloadItem(
      'net-new',
      newBusinessUUID,
      selectedOperandsLookup.value.newBusiness,
    );

    const recurringBusinessSetting = createPayloadItem(
      'existing',
      recurringBusinessUUID,
      selectedOperandsLookup.value.existingBusiness,
    );

    if (!newBusinessSetting || !recurringBusinessSetting) {
      return null;
    }
    return {
      items: [newBusinessSetting, recurringBusinessSetting],
    };
  }

  const fetchOpportunityFilters = async () => {
    const { data, error } = await crossbeamApi.GET('/v0.1/opportunity-filters');

    if (error) throw error;

    businessSettings.value = data?.items as unknown as [
      BusinessSettingDefinition,
      BusinessSettingDefinition,
    ];
  };

  async function saveSettings() {
    if (!isValidSettings.value || !selectedFeedId.value) return;

    try {
      const payload = buildPayload();

      if (!payload) {
        throw new Error('No settings defined');
      }

      const { error } = await crossbeamApi.PUT('/v0.1/opportunity-filters', {
        body: payload as OpportunityFiltersPayload,
      });

      await Promise.all([
        fetchOpportunityFilters(),
        updateOrgInfo(selectedFeedId.value),
      ]);

      if (error) {
        throw error;
      }
      await loadAll();
    } catch (error) {
      captureException(error);
    }
  }

  const sortByLabel = (
    a: OperandOption | SourceOption,
    b: OperandOption | SourceOption,
  ) => a.label.localeCompare(b.label);

  async function fetchSourceData() {
    const field = selectedOpportunityFieldId.value;

    if (!field) return [];

    const { data, error } = await crossbeamApi.POST(
      '/v0.1/search/source-data',
      {
        body: {
          field,
          query: null,
        },
      },
    );

    if (error) {
      captureException(error);
      return [];
    }

    return (data ?? [])
      .map((label) => ({ label, value: label }))
      .sort(sortByLabel);
  }

  const BOOLEAN_OPTIONS = [
    { label: 'True', value: 'true' },
    { label: 'False', value: 'false' },
  ];

  type CRMWithPickList = StrictExclude<
    CRMFeedType,
    'snowflake' | 'databricks' | 'trace' | 'external_file_data'
  >;
  const PICKLIST_KEYS_OR_VALUES_BY_CRM: Record<
    CRMWithPickList,
    'keys' | 'values'
  > = {
    hubspot_v3: 'keys',
    salesforce: 'keys',
    pipedrive: 'values',
    microsoft_dynamics: 'values',
    dell_spil: 'keys',
    aws_partner_central: 'keys',
  };

  function buildOptions(field: SourceField) {
    const feedType = selectedFeedType.value;
    if (!feedType) return [];
    if (field.data_type === 'boolean') {
      return BOOLEAN_OPTIONS;
    }

    const entries = Object.entries(field.options ?? {});

    const pick = PICKLIST_KEYS_OR_VALUES_BY_CRM[feedType] ?? 'keys';

    const opts = entries.map(([key, val]) =>
      pick === 'keys' ? { label: key, value: key } : { label: val, value: val },
    );

    return opts.sort(sortByLabel);
  }

  async function fetchFilterableSources(
    sourceId: number,
  ): Promise<SourceOption[]> {
    if (!selectedFeedType.value) return [];
    const { data, error } = await crossbeamApi.GET(
      '/v0.2/sources/{id}/filterable-sources',
      { params: { path: { id: sourceId } } },
    );

    if (error) {
      captureException(error);
      return [];
    }

    const fields =
      (data?.items as FilterableSource[]).find(
        (source) => source.mdm_type === 'deal',
      )?.fields ?? [];

    return fields
      ?.filter(
        (field) =>
          field.data_type === 'boolean' || field.data_type === 'string',
      )
      .map((field) => ({
        label: field.display_name,
        value: field.id as number,
        options: buildOptions(field),
        dataType: field.data_type as 'string' | 'boolean',
      }))
      .filter((field) => field.options.length > 1)
      .sort(sortByLabel);
  }

  async function setOperandOptions(fieldId: number) {
    const selectedField = opportunityFieldOptions.value.find(
      (field) => field.value === fieldId,
    );

    if (
      selectedField?.dataType === 'string' &&
      !selectedField?.options.length
    ) {
      operandOptions.value = await fetchSourceData();
      return;
    }
    operandOptions.value = selectedField?.options ?? [];
  }

  function updateOperandsLookup(
    businessSetting: BusinessSettingDefinition,
    type: 'newBusiness' | 'existingBusiness',
  ) {
    const filterDefinition = businessSetting.source_filter_expressions;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const [_, filterVals] = Object.entries(filterDefinition)[0]!;
    const filter = filterVals.filter_parts[0];

    if (!filter) return;

    /* use string for boolean values to play well with select component */
    const operand =
      typeof filter.operand[0] === 'boolean'
        ? (filter.operand[0]?.toString() as 'true' | 'false')
        : filter.operand;
    selectedOperandsLookup.value[type] = operand;

    /* same source_field_id on both settings, just grab the first */
    if (type === 'newBusiness') {
      const isDataSourceSettingsNewDisabledCrm =
        route.name === 'data-sources-settings-new' &&
        Number(route.params.id) !== selectedFeedId.value;
      if (isDataSourceSettingsNewDisabledCrm) return;
      selectedOpportunityFieldId.value = filter.source_field_id ?? null;
    }
  }

  async function loadAll() {
    loading.value = true;
    await sourcesStore.readySync;
    if (!businessSettings.value?.length) {
      await fetchOpportunityFilters();
    }
    try {
      const [newBusinessSetting, existingBusinessSetting] = [
        businessSettings.value.find((setting) =>
          setting.name.startsWith('net-new'),
        ),
        businessSettings.value.find((setting) =>
          setting.name.startsWith('existing'),
        ),
      ];

      selectedFeedId.value = preferredCRMFeed.value?.id ?? null;

      if (
        !selectedFeedId.value ||
        !newBusinessSetting ||
        !existingBusinessSetting
      )
        return;

      updateOperandsLookup(
        newBusinessSetting as BusinessSettingDefinition,
        'newBusiness',
      );
      updateOperandsLookup(
        existingBusinessSetting as BusinessSettingDefinition,
        'existingBusiness',
      );
    } catch (error) {
      captureException(error);
      resetAll();
    } finally {
      loading.value = false;
      snapshot.value = {
        feedId: selectedFeedId.value,
        fieldId: selectedOpportunityFieldId.value,
        operands: {
          newBusiness: [...selectedOperandsLookup.value.newBusiness],
          existingBusiness: [...selectedOperandsLookup.value.existingBusiness],
        },
      };
    }
  }

  onBeforeMount(loadAll);
  onBeforeUnmount(() => {
    businessSettings.value = [];
  });

  type MaybeStrings = string | string[];
  function sameStrings(a: MaybeStrings, b: MaybeStrings) {
    const arrA = Array.isArray(a) ? a : a.split('');
    const arrB = Array.isArray(b) ? b : b.split('');

    if (arrA.length !== arrB.length) return false;
    return isEqual(sortBy(arrA), sortBy(arrB));
  }

  const haveSettingsChanged = computed(() => {
    // until we’ve taken a snapshot, nothing is “dirty”
    if (loading.value || snapshot.value === null || !isValidSettings.value) {
      return false;
    }
    if (
      selectedFeedId.value !== snapshot.value.feedId ||
      selectedOpportunityFieldId.value !== snapshot.value.fieldId
    ) {
      return true;
    }

    const { newBusiness, existingBusiness } = selectedOperandsLookup.value;
    const { newBusiness: oldNew, existingBusiness: oldExisting } =
      snapshot.value.operands;

    if (!sameStrings(newBusiness, oldNew)) return true;
    return !sameStrings(existingBusiness, oldExisting);
  });

  watch(loading, (isLoading) => {
    if (!isLoading) {
      snapshot.value = {
        feedId: selectedFeedId.value,
        fieldId: selectedOpportunityFieldId.value,
        operands: {
          newBusiness: [...selectedOperandsLookup.value.newBusiness],
          existingBusiness: [...selectedOperandsLookup.value.existingBusiness],
        },
      };
    }
  });

  // Update selectedFeedId when preferred CRM changes
  watch(preferredCRMFeed, (newFeed) => {
    if (newFeed?.id && selectedFeedId.value !== newFeed.id) {
      selectedFeedId.value = newFeed.id;
    }
  });

  watch(disabled, async (newDisabled) => {
    if (
      !newDisabled &&
      selectedFeedId.value === Number(route?.params?.id) &&
      snapshot.value?.fieldId
    ) {
      await loadAll();
    }
  });

  return {
    loading,
    disabled,
    selectedFeedId,
    selectedOpportunityFieldId,
    selectedOperandsLookup,
    feedOptions,
    opportunityFieldOptions,
    newBusinessOptions,
    operandOptions,
    recurringBusinessOptions,
    isValidSettings,
    isMultiSelect,
    isDataSourceInputDisabled,
    dealSourceFromSelectedFeed,
    haveSettingsChanged,
    snapshot,
    buildPayload,
    fetchFilterableSources,
    resetAll,
    resetOperands,
    saveSettings,
    setOperandOptions,
  };
}
